import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { resolveLimits } from '@traaker/shared';
import { api } from '../services/api';
import { pushDataLayerEvent } from '../utils/analytics';
import { runTour, atRoute, type StepDef, type TourCtx } from './tourRunner';

// Post-onboarding product tour. A single continuous walkthrough that spans
// multiple routes, driven by the shared runner in ./tourRunner.
//
// Two flows, chosen by tier:
//   - AGENCY:    full tour incl. brand picker, credits, audit caps, reports,
//                white-label, per-brand billing + credit ledger.
//   - SELF-SERVE (basic/pro/elite/enterprise): the same product story, trimmed
//                of agency-only surfaces (credits, caps, reports, multi-brand
//                management) and reframed around quotas + automatic schedules.
//
// Auto-fires exactly once after the brand wizard (router state `justOnboarded`
// + unset `user.tourState.dashboardTourSeenAt`). The Settings "Revoir la visite"
// link calls start() directly, bypassing both gates.
//
// The E-Commerce module gets its OWN, much more detailed tour — see
// ./useEcommerceTour.ts, launched from the sidebar's "Nouveau" info popover.

// Step `title` / `description` hold i18n KEYS (under the `tour.` prefix),
// resolved with `i18n.t()` at step-build time so the active UI language wins.

const competitorsVisible = (ctx: TourCtx) => {
  const q = ctx.limits?.competitors;
  return !(typeof q === 'number' && q === 0);
};
const tasksVisible = (ctx: TourCtx) => ctx.flags?.tasksEnabled === true;
const studioVisible = (ctx: TourCtx) => ctx.flags?.newsWatchEnabled === true;

// ── AGENCY flow ──────────────────────────────────────────────────────────
const AGENCY_STEPS: StepDef[] = [
  {
    element: '[data-tour="dashboard-header"]',
    route: '/',
    side: 'bottom',
    align: 'start',
    title: 'tour.welcome.title',
    description: 'tour.agency.welcome.desc',
  },
  {
    element: '[data-tour="brand-selector"]',
    route: '/',
    side: 'bottom',
    align: 'start',
    title: 'tour.brandSelector.title',
    description: 'tour.brandSelector.desc',
  },
  {
    element: '[data-tour="credit-balance"]',
    route: '/',
    side: 'bottom',
    align: 'end',
    title: 'tour.credits.title',
    description: 'tour.credits.desc',
  },
  {
    centered: true,
    route: '/audits',
    title: 'tour.websiteAudit.title',
    description: 'tour.websiteAudit.desc',
  },
  {
    element: '[data-tour="schedule-control"]',
    route: '/audits',
    side: 'bottom',
    align: 'end',
    title: 'tour.schedule.title',
    description: 'tour.schedule.desc',
  },
  {
    element: '[data-tour="cap-control"]',
    route: '/audits',
    side: 'bottom',
    align: 'end',
    title: 'tour.cap.title',
    description: 'tour.cap.desc',
  },
  {
    centered: true,
    route: '/earned-media',
    title: 'tour.earnedMedia.title',
    description: 'tour.earnedMedia.desc',
  },
  {
    element: '[data-tour="schedule-control"]',
    route: '/earned-media',
    side: 'bottom',
    align: 'end',
    title: 'tour.earnedMediaSettings.title',
    description: 'tour.earnedMediaSettings.desc',
  },
  {
    centered: true,
    route: '/brand/narrative',
    title: 'tour.brandImage.title',
    description: 'tour.brandImage.desc',
  },
  {
    element: '[data-tour="prompt-config"]',
    route: '/brand/narrative',
    // Centered popover (like the "Média & presse" step) — the button is still
    // highlighted via the stage cutout, but the popover is CSS-pinned to the
    // viewport center so it can never land off-screen. Anchored placement was
    // unreliable here: the long description makes a ~380px popover that doesn't
    // fit below a vertically-centered button on a tall (results-loaded) page.
    centered: true,
    title: 'tour.promptConfig.title',
    description: 'tour.promptConfig.desc',
  },
  {
    element: '[data-tour="nav:/brand/authority"]',
    centered: true,
    side: 'right',
    align: 'start',
    title: 'tour.authorityReferral.title',
    description: 'tour.authorityReferral.desc',
  },
  {
    centered: true,
    route: '/competitors',
    title: 'tour.competitors.title',
    description: 'tour.competitors.desc',
    visible: competitorsVisible,
  },
  {
    centered: true,
    route: '/news-watch',
    title: 'tour.studio.title',
    description: 'tour.agency.studio.desc',
    visible: studioVisible,
  },
  // The E-Commerce module is intentionally NOT covered here: it has its own
  // detailed tour (./useEcommerceTour.ts), reachable from the sidebar's
  // "Nouveau" info popover. One centered step points agencies at it.
  {
    element: '[data-tour="nav:/product/catalog"]',
    centered: true,
    side: 'right',
    align: 'start',
    title: 'tour.productModule.title',
    description: 'tour.productModule.desc',
    visible: (ctx: TourCtx) => ctx.flags?.productModuleEnabled === true,
  },
  {
    centered: true,
    route: '/reports/library',
    title: 'tour.reports.title',
    description: 'tour.reports.desc',
  },
  {
    element: '[data-tour="nav:/reports/audit"]',
    centered: true,
    side: 'right',
    align: 'start',
    title: 'tour.reportTemplate.title',
    description: 'tour.reportTemplate.desc',
  },
  {
    element: '[data-tour="nav:/strategy"]',
    centered: true,
    side: 'right',
    align: 'start',
    title: 'tour.brandKnowledge.title',
    description: 'tour.brandKnowledge.desc',
  },
  {
    element: '[data-tour="nav:/geo-strategy"]',
    centered: true,
    side: 'right',
    align: 'start',
    title: 'tour.geoStrategy.title',
    description: 'tour.geoStrategy.desc',
  },
  {
    element: '[data-tour="nav:/tasks"]',
    centered: true,
    side: 'right',
    align: 'start',
    title: 'tour.tasks.title',
    description: 'tour.tasks.desc',
    visible: tasksVisible,
  },
  {
    element: '[data-tour="brands-manage"]',
    centered: true,
    route: '/settings?tab=brands',
    title: 'tour.manageBrands.title',
    description: 'tour.manageBrands.desc',
  },
  {
    element: '[data-tour="credit-ledger"]',
    centered: true,
    route: '/settings?tab=billing',
    title: 'tour.billing.title',
    description: 'tour.billing.desc',
  },
];

// ── SELF-SERVE flow (basic / pro / elite / enterprise) ────────────────────
const SELF_SERVE_STEPS: StepDef[] = [
  {
    element: '[data-tour="dashboard-header"]',
    route: '/',
    side: 'bottom',
    align: 'start',
    title: 'tour.welcome.title',
    description: 'tour.selfServe.welcome.desc',
  },
  {
    centered: true,
    route: '/audits',
    title: 'tour.websiteAudit.title',
    description: 'tour.websiteAudit.desc',
  },
  {
    element: '[data-tour="schedule-control"]',
    route: '/audits',
    side: 'bottom',
    align: 'end',
    title: 'tour.scheduleAuto.title',
    description: 'tour.scheduleAuto.desc',
  },
  {
    centered: true,
    route: '/earned-media',
    title: 'tour.earnedMedia.title',
    description: 'tour.earnedMedia.desc',
  },
  {
    centered: true,
    route: '/brand/narrative',
    title: 'tour.brandImage.title',
    description: 'tour.brandImage.desc',
  },
  {
    element: '[data-tour="prompt-config"]',
    route: '/brand/narrative',
    // Centered popover (like the "Média & presse" step) — the button is still
    // highlighted via the stage cutout, but the popover is CSS-pinned to the
    // viewport center so it can never land off-screen. Anchored placement was
    // unreliable here: the long description makes a ~380px popover that doesn't
    // fit below a vertically-centered button on a tall (results-loaded) page.
    centered: true,
    title: 'tour.promptConfig.title',
    description: 'tour.promptConfig.desc',
  },
  {
    element: '[data-tour="nav:/brand/authority"]',
    centered: true,
    side: 'right',
    align: 'start',
    title: 'tour.authorityReferral.title',
    description: 'tour.authorityReferral.desc',
  },
  {
    centered: true,
    route: '/competitors',
    title: 'tour.competitors.title',
    description: 'tour.competitors.desc',
    visible: competitorsVisible,
  },
  {
    centered: true,
    route: '/news-watch',
    title: 'tour.studio.title',
    description: 'tour.selfServe.studio.desc',
    visible: studioVisible,
  },
  {
    element: '[data-tour="nav:/strategy"]',
    centered: true,
    side: 'right',
    align: 'start',
    title: 'tour.brandKnowledge.title',
    description: 'tour.brandKnowledge.desc',
  },
  {
    element: '[data-tour="nav:/geo-strategy"]',
    centered: true,
    side: 'right',
    align: 'start',
    title: 'tour.geoStrategy.title',
    description: 'tour.geoStrategy.desc',
  },
  {
    element: '[data-tour="nav:/tasks"]',
    centered: true,
    side: 'right',
    align: 'start',
    title: 'tour.tasks.title',
    description: 'tour.tasks.desc',
    visible: tasksVisible,
  },
  {
    element: '[data-tour="quotas-card"]',
    centered: true,
    route: '/settings',
    title: 'tour.quotas.title',
    description: 'tour.quotas.desc',
  },
];

export function useDashboardTour() {
  const { tenant, refreshProfile } = useAuth();
  const navigate = useNavigate();

  const run = useCallback(
    (opts: { markSeen: boolean }) => {
      const ctx: TourCtx = {
        flags: tenant?.settings?.featureFlags ?? {},
        limits: tenant ? resolveLimits(tenant.tier, tenant.settings?.quotaOverrides) : null,
        tier: tenant?.tier ?? 'basic',
      };

      runTour(ctx.tier === 'agency' ? AGENCY_STEPS : SELF_SERVE_STEPS, ctx, {
        navigate,
        onDestroyed: (finished) => {
          // Non-agency tours end on a Settings card (quotas). On a legitimate
          // finish, bring the user back to the dashboard — the natural home
          // base — rather than leaving them stranded on the Settings page.
          if (finished && ctx.tier !== 'agency' && !atRoute('/')) {
            navigate('/');
          }
          // Funnel event: ONLY a real completion ("Terminer" on the last step),
          // never an early close/✕/Échap, and only on the auto-fired first-run
          // tour (`markSeen`) so a Settings replay can't send a duplicate.
          if (finished && opts.markSeen) {
            pushDataLayerEvent('product_tour_complete');
          }
          if (opts.markSeen) {
            // Fire-and-forget: stamp the flag server-side, then refresh the
            // profile so a same-session re-mount doesn't re-trigger the tour.
            api
              .patch('/users/me/tour-seen', {})
              .then(() => refreshProfile())
              .catch(() => {});
          }
        },
      });
    },
    [tenant, refreshProfile, navigate],
  );

  // Auto-run after onboarding: only if never seen. Marks seen on completion.
  const maybeAutoStart = useCallback(() => {
    if (!tenant) return;
    run({ markSeen: true });
  }, [tenant, run]);

  // Manual replay (Settings link): always runs, never re-stamps the flag.
  const start = useCallback(() => {
    run({ markSeen: false });
  }, [run]);

  return { maybeAutoStart, start };
}
